<script setup lang="ts">
import { computed, ref } from 'vue';

import { eqsApi } from '@/api/eqs';
import { notify } from '@/services/notice';
import { errorMessage } from '@/utils/format';

const emit = defineEmits<{ applied: [] }>();

interface Proposal {
  itemId: string;
  source: string;
  projectNo: string | null;
  projectName: string | null;
  title: string;
  currentStatus: string;
  status: string | null;
  actualDate: string | null;
  evidence: string;
  confidence: 'high' | 'low';
}

const open = ref(false);
const text = ref('');
const parsing = ref(false);
const applying = ref(false);
const proposals = ref<Proposal[]>([]);
const unmatched = ref<string[]>([]);
const picked = ref<Set<string>>(new Set());
const scanned = ref(0);
/** 未完成任务超过后端上限时只取了前一批，必须说出来——静默截断等于漏更新 */
const truncated = ref(false);

const STATUS_LABEL: Record<string, string> = {
  pending: '待处理',
  in_progress: '处理中',
  blocked: '受阻',
  completed: '已完成',
};

const SAMPLE = 'X04C 的 T0 试模昨天完成了；D11 出风口饰条的 DFMEA 评审这周开始做；'
  + '秦岭 OP3 的模具转移卡在供应商那边，先标受阻。';

async function parse() {
  if (!text.value.trim()) {
    notify('error', '请先填写内容', '把这段时间的进展按平时说话的方式写下来即可。');
    return;
  }
  parsing.value = true;
  proposals.value = [];
  unmatched.value = [];
  try {
    const result = await eqsApi.parseQuickUpdate(text.value);
    proposals.value = (result.proposals ?? []) as unknown as Proposal[];
    unmatched.value = result.unmatched ?? [];
    scanned.value = result.scanned ?? 0;
    truncated.value = Boolean(result.truncated);
    // 高置信默认勾上，低置信要人主动确认
    picked.value = new Set(
      proposals.value.filter((item) => item.confidence === 'high').map((item) => item.itemId),
    );
    if (!proposals.value.length) {
      notify('error', '没有匹配到任务', result.note ?? '换个说法，或带上项目编号和任务名试试。');
    } else {
      notify('success', `匹配到 ${proposals.value.length} 条`, '请逐条核对依据后再写入。');
    }
  } catch (caught) {
    notify('error', '解析失败', errorMessage(caught));
  } finally {
    parsing.value = false;
  }
}

function toggle(itemId: string) {
  const next = new Set(picked.value);
  if (next.has(itemId)) next.delete(itemId);
  else next.add(itemId);
  picked.value = next;
}

const chosen = computed(() => proposals.value.filter((item) => picked.value.has(item.itemId)));

async function apply() {
  if (!chosen.value.length) return;
  applying.value = true;
  try {
    const result = await eqsApi.applyQuickUpdate(chosen.value.map((item) => ({
      itemId: item.itemId,
      status: item.status,
      actualDate: item.actualDate,
      // 标完成要有证据（0930）：没挂交付物的任务，这句原话就是完成说明；挂了交付物的照样要文件
      evidence: item.evidence || null,
    })));
    const failed = result.failed ?? 0;
    notify(
      failed ? 'error' : 'success',
      `已更新 ${result.applied ?? 0} 条${failed ? `，${failed} 条未成功` : ''}`,
      failed
        ? (result.results ?? []).filter((r) => !r.ok).map((r) => r.message).join('；')
        : '状态与实际日期已写入，并记入进度事件与审计。',
    );
    proposals.value = [];
    text.value = '';
    picked.value = new Set();
    emit('applied');
  } catch (caught) {
    notify('error', '写入失败', errorMessage(caught));
  } finally {
    applying.value = false;
  }
}
</script>

<template>
  <section class="quick-update">
    <header>
      <div>
        <span>QUICK UPDATE</span>
        <h3>用一段话批量更新进度</h3>
      </div>
      <button class="button" type="button" @click="open = !open">
        {{ open ? '收起' : '展开' }}
      </button>
    </header>

    <template v-if="open">
      <p class="qu-intro">
        按平时说话的方式写下最近的进展，系统会匹配到你名下的任务，<b>确认后才写入</b>。
        只在你自己的未完成任务里匹配，不会动别人的。
      </p>

      <textarea
        v-model="text"
        rows="4"
        :placeholder="SAMPLE"
        aria-label="工作进展描述"
      ></textarea>

      <div class="qu-actions">
        <button class="button button-primary" type="button" :disabled="parsing" @click="parse">
          {{ parsing ? '匹配中…（约 10-20 秒）' : '匹配我的任务' }}
        </button>
        <button
          v-if="!text"
          class="button"
          type="button"
          @click="text = SAMPLE"
        >填入示例</button>
        <small v-if="scanned">已在 {{ scanned }} 条未完成任务中匹配</small>
      </div>

      <p v-if="truncated" class="qu-truncated">
        你的未完成任务超过 {{ scanned }} 条，本次只取了按截止日期最早的前 {{ scanned }} 条来匹配。
        没被匹配到的任务不代表没有——靠后的任务请分批写，或直接在下方任务队列里改。
      </p>

      <ul v-if="unmatched.length" class="qu-unmatched">
        <li v-for="(line, index) in unmatched" :key="index">
          没能对上任务：{{ line }}
        </li>
      </ul>

      <div v-if="proposals.length" class="qu-proposals">
        <div class="qu-head">
          <b>{{ proposals.length }} 条建议，已选 {{ chosen.length }} 条</b>
          <span>低置信的默认没勾选，请核对「依据」一栏确认匹配是否正确。</span>
        </div>

        <article
          v-for="item in proposals"
          :key="item.itemId"
          :class="{ picked: picked.has(item.itemId), low: item.confidence === 'low' }"
        >
          <label>
            <input
              type="checkbox"
              :checked="picked.has(item.itemId)"
              :aria-label="`选择 ${item.title}`"
              @change="toggle(item.itemId)"
            >
            <b>{{ item.title }}</b>
            <span class="qu-project">{{ item.projectName || item.projectNo || '非项目' }}</span>
            <em v-if="item.confidence === 'low'" class="qu-low">待确认</em>
          </label>
          <dl>
            <dt>变更</dt>
            <dd>
              <span v-if="item.status">
                {{ STATUS_LABEL[item.currentStatus] || item.currentStatus }}
                →
                <b>{{ STATUS_LABEL[item.status] || item.status }}</b>
              </span>
              <span v-if="item.actualDate">实际完成 <b>{{ item.actualDate }}</b></span>
            </dd>
            <dt>依据</dt>
            <dd class="qu-evidence">「{{ item.evidence || '（模型未给出依据）' }}」</dd>
          </dl>
        </article>

        <footer>
          <button
            class="button button-primary"
            type="button"
            :disabled="applying || !chosen.length"
            @click="apply"
          >{{ applying ? '写入中…' : `确认写入 ${chosen.length} 条` }}</button>
          <button class="button" type="button" :disabled="applying" @click="proposals = []">放弃</button>
        </footer>
      </div>
    </template>
  </section>
</template>

<style scoped>
.quick-update { display: grid; gap: 10px; padding: 14px 16px; margin-bottom: 14px; border: 1px solid var(--line); border-left: 3px solid var(--blue); background: var(--white); }
.quick-update > header { display: flex; align-items: flex-end; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
.quick-update header span { color: var(--muted); font: 700 12px var(--display); letter-spacing: .08em; }
.quick-update header h3 { margin: 2px 0 0; font-size: 16px; }
.qu-intro { margin: 0; color: var(--muted); font-size: 13px; line-height: 1.8; }
.quick-update textarea { width: 100%; padding: 9px 11px; border: 1px solid var(--line); background: var(--white); font: inherit; font-size: 13px; line-height: 1.8; resize: vertical; }
.qu-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.qu-actions small { color: var(--muted); font-size: 12px; }
.qu-truncated { margin: 0; padding: 8px 10px; border-left: 3px solid var(--warning-graphic); background: #fdf6ec; color: #7a4a10; font-size: 12px; line-height: 1.8; }
.qu-unmatched { margin: 0; padding: 8px 10px 8px 26px; border-left: 3px solid var(--warning-graphic); background: #fdf6ec; color: #7a4a10; font-size: 12px; line-height: 1.8; }
.qu-proposals { display: grid; gap: 8px; }
.qu-head { display: grid; gap: 2px; }
.qu-head b { font-size: 13px; }
.qu-head span { color: var(--muted); font-size: 12px; }
.qu-proposals article { padding: 9px 11px; border: 1px solid var(--line); background: var(--paper); opacity: .6; }
.qu-proposals article.picked { opacity: 1; border-color: var(--blue); background: var(--white); }
.qu-proposals article.low { border-left: 3px solid var(--warning-graphic); }
.qu-proposals label { display: flex; align-items: center; gap: 8px; cursor: pointer; }
.qu-proposals label b { font-size: 13px; }
.qu-project { padding: 1px 6px; border: 1px solid var(--line); background: var(--paper); color: var(--muted); font-size: 12px; }
.qu-low { padding: 0 5px; border-radius: 3px; background: #fdf3e3; color: var(--warning); font-size: 12px; font-style: normal; font-weight: 700; }
.qu-proposals dl { display: grid; grid-template-columns: 40px 1fr; gap: 3px 8px; margin: 6px 0 0 24px; font-size: 12px; }
.qu-proposals dt { color: var(--muted); }
.qu-proposals dd { margin: 0; }
.qu-proposals dd span { margin-right: 12px; }
.qu-evidence { color: var(--muted); font-style: italic; }
.qu-proposals footer { display: flex; gap: 8px; }
</style>
